import { useState, type ChangeEvent } from "react";
import { Badge, Button, Icon } from "../../ds/DS";
import { api } from "../../lib/api";
import type { PageProps } from "../Portal";
import { buildDone, buildTodo, type TodoItem } from "../todo";
import { PageHead } from "./parts";
import { NewTicket } from "./ticketParts";

// Client To do (handoff C2 `isTodo`): one list of everything waiting on the
// client — ticket replies, release and milestone sign-offs, document and
// action tasks, change-request quotes — overdue first. Each action uses the
// existing endpoints; files attached here are ordinary documents, so they
// also show in Files.

export function ToDo(props: PageProps) {
  const { projects, pid, openItem, go, reload, toast } = props;
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const todo = buildTodo(props);
  const done = buildDone(props);

  const run = async (key: string, fn: () => Promise<unknown>, msg: string) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      reload();
      toast(msg);
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const attach = (t: TodoItem) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked again after an error
    if (!file || !t.task) return;
    const form = new FormData();
    form.set("file", file);
    void run(
      t.key,
      () => api.upload(`/tasks/${t.task?.id}/submit`, form),
      `${file.name} attached. Send it when you're ready.`,
    );
  };

  const actions = (t: TodoItem) => {
    const isBusy = busy === t.key;
    switch (t.kind) {
      case "reply":
        return (
          <Button icon="arrow-right" onClick={() => t.ticket && openItem("tickets", t.ticket.id)}>
            Reply
          </Button>
        );
      case "release":
        return (
          <Button
            icon="arrow-right"
            onClick={() => (t.release ? openItem("releases", t.release.id) : go("releases"))}
          >
            Review and approve
          </Button>
        );
      case "milestone":
        return (
          <Button
            icon="check"
            disabled={isBusy}
            onClick={() =>
              void run(
                t.key,
                () => api.post(`/milestones/${t.milestone?.id}/signoff`),
                `${t.title} signed off. Thanks.`,
              )
            }
          >
            Sign off
          </Button>
        );
      case "document": {
        const files = t.task?.documents.length ?? 0;
        const canAdd = files === 0 || !!t.task?.allowMultiple;
        return (
          <>
            {canAdd ? (
              <label
                className={"ds-btn ds-btn--secondary ds-btn--md todo-attach" + (isBusy ? " is-busy" : "")}
              >
                <input type="file" hidden disabled={isBusy} onChange={attach(t)} />
                <Icon name="paperclip" size={16} />
                <span>{isBusy ? "Uploading…" : files ? "Add another" : "Attach file"}</span>
              </label>
            ) : null}
            <Button
              icon="check"
              variant={files ? "primary" : "secondary"}
              disabled={!files || isBusy}
              onClick={() =>
                void run(
                  t.key,
                  () => api.patch(`/tasks/${t.task?.id}`, { state: "Submitted" }),
                  "Thanks. We'll check it and confirm.",
                )
              }
            >
              Send
            </Button>
          </>
        );
      }
      case "action":
        return (
          <Button
            icon="check"
            disabled={isBusy}
            onClick={() =>
              void run(
                t.key,
                () => api.patch(`/tasks/${t.task?.id}`, { state: "Submitted" }),
                "Thanks. We'll check it and confirm.",
              )
            }
          >
            Done
          </Button>
        );
      case "quote": {
        const ref = t.cr?.ref || "Change request";
        const decide = (ok: boolean) =>
          void run(
            t.key,
            () => api.patch(`/change-requests/${t.cr?.id}`, { state: ok ? "Approved" : "Declined" }),
            ok
              ? `${ref} approved. We'll confirm the release date by tomorrow.`
              : `${ref} declined. Nothing will be billed.`,
          );
        return (
          <>
            <Button icon="check" disabled={isBusy} onClick={() => decide(true)}>
              Approve
            </Button>
            <Button variant="secondary" icon="x" disabled={isBusy} onClick={() => decide(false)}>
              Decline
            </Button>
          </>
        );
      }
    }
  };

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="TO DO"
        title="To do"
        sub="Everything we need from you, in one list. Most take a minute."
      />

      {todo.map((t) => (
        <div key={t.key} className={"todo-card" + (t.overdue ? " is-late" : "")}>
          <span className="todo-rule" />
          <div className="todo-main">
            <div className="row-8">
              <span className="todo-label">{t.label}</span>
              {t.overdue ? (
                <Badge tone="danger" dot>
                  Overdue
                </Badge>
              ) : null}
            </div>
            <div className="todo-title">{t.title}</div>
            {t.why ? <p className="todo-why">{t.why}</p> : null}
            {t.extra ? <div className="todo-extra">{t.extra}</div> : null}
            {t.task?.documents.length ? (
              <div className="row-8 todo-files">
                {t.task.documents.map((d) => (
                  <a key={d.id} className="file-chip" href={`/api/documents/${d.id}/download`}>
                    <Icon name="file-text" size={14} />
                    {d.name}
                  </a>
                ))}
              </div>
            ) : null}
            {t.meta ? <div className="todo-meta">{t.meta}</div> : null}
          </div>
          <div className="todo-actions">{actions(t)}</div>
        </div>
      ))}

      {todo.length === 0 ? (
        <div className="todo-empty">
          <span className="todo-empty-ico">
            <Icon name="circle-check" size={26} />
          </span>
          <div className="todo-empty-title">You're all caught up</div>
          <div className="todo-empty-line">
            Everything is with us. We'll email you when we need something.
          </div>
        </div>
      ) : null}

      {done.length ? (
        <div className="stack-12">
          <div className="todo-done-head">Done recently</div>
          <div className="list-card">
            {done.map((d) => (
              <div key={d.key} className="todo-done-row">
                <span className="todo-done-ico">
                  <Icon name="check" size={16} />
                </span>
                <span className="todo-done-title">{d.title}</span>
                <span className="todo-done-line">{d.line}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <button type="button" className="link-btn todo-ask" onClick={() => setAsking(true)}>
        Not sure about something? Ask us →
      </button>

      <NewTicket
        open={asking}
        isAdmin={false}
        projects={projects}
        defaultPid={pid}
        onClose={() => setAsking(false)}
        onCreated={(id) => {
          reload();
          toast("Ticket raised. You'll get a reply within one working day.");
          openItem("tickets", id);
        }}
      />
    </div>
  );
}
